import { type GuestHomeResponse } from "@core/home/types";
import { trans, plainTrans } from "@mongez/localization";
import { InViewport } from "../../../core/shared/components/InViewPort";
import { AnnouncementPanel } from "../../shared/components/AnnouncementPanel";
import { TypewriterText } from "../../shared/components/TypewriterText";
import { CategoriesSection } from "../components/CategoriesSection";
import { GradeCard } from "../components/GradeCard";
import { GradesCategoriesSection } from "../components/GradesCategoriesSection";
import { HeroSection } from "../components/HeroSection";
import { InstructorsSection } from "../components/InstructorsSection";
import { LatestLessonsSection } from "../components/LatestLessonsSection";
import { LazySessionsSection } from "../components/LazySessionsSection";
import { OffersCarousel } from "../components/OffersCarousel";
import { PopularLessonsSection } from "../components/PopularLessonsSection";
import { SubjectSelection } from "../components/SubjectSelection";
import { useActiveSubject } from "../hooks/use-active-subject";

interface GuestHomePageContentProps {
  data: GuestHomeResponse;
}

export function GuestHomePageContent({ data }: GuestHomePageContentProps) {
  const { activeSubject, handleSubjectChange } = useActiveSubject({
    subjects: data.grades?.subjects || [],
  });

  // Determine scenario based on available data
  const hasMultipleSubjects =
    data.grades?.subjects && data.grades.subjects.length > 1;
  const hasGrades = data.grades?.grades && data.grades.grades.length > 0;
  const hasCategories =
    data.grades?.categories && data.grades.categories.length > 0;
  const hasInstructors =
    data.grades?.instructors && data.grades.instructors.length > 0;

  const offers = data.grades?.offers || [];

  // Scenario 2: Single subject or no subjects - show grades directly
  if (!hasMultipleSubjects) {
    return (
      <div className="container mx-auto space-y-12 px-4 py-8 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <HeroSection
          heroImage={data.heroImage}
          hero={data.hero}
          isGuest={true}
        />

        {/* About Section */}
        {data.about?.content && (
          <div className="flex flex-col gap-8 rounded-xl bg-white/50 p-6 dark:bg-gray-800/50 md:flex-row md:p-8">
            <div className="flex-1">
              <h2 className="mb-3 text-3xl font-bold text-gray-800 dark:text-gray-200 md:text-4xl">
                {data.about.title}
              </h2>
              <TypewriterText
                text={data.about.content}
                speed={20}
                className="text-lg leading-relaxed text-gray-600 dark:text-gray-300"
              />
            </div>
            {data.about.image && (
              <div className="relative">
                <img
                  src={data.about.image.url}
                  alt="about"
                  className="h-[300px] w-full rounded-lg object-cover md:h-[400px]"
                />
              </div>
            )}
          </div>
        )}

        {/* Special Offers Section */}
        {offers && offers.length > 0 && (
          <OffersCarousel
            offers={offers}
            title={plainTrans("home.specialOffers")}
            description={trans("home.offersDescription")}
          />
        )}

        {/* Announcements */}
        {data.announcements?.length > 0 && (
          <AnnouncementPanel announcement={data.announcements[0]} />
        )}

        {hasGrades && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="mb-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-4xl font-bold text-transparent">
                {trans("grades.browseByGrade")}
              </h2>
              <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-400">
                {trans("grades.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {data.grades?.grades?.map((grade, index) => (
                <GradeCard key={grade.id} grade={grade} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Popular content for social proof */}
        {data.grades?.popularLessons &&
          data.grades.popularLessons.length > 0 && (
            <PopularLessonsSection lessons={data.grades.popularLessons} />
          )}

        {/* Grades Categories Section */}
        {data.grades?.gradesCategories &&
          data.grades.gradesCategories.length > 0 && (
            <GradesCategoriesSection
              gradesCategories={data.grades.gradesCategories}
              className="space-y-12"
            />
          )}

        {/* Fresh content to encourage exploration */}
        {data.grades?.latestLessons && data.grades.latestLessons.length > 0 && (
          <LatestLessonsSection lessons={data.grades.latestLessons} />
        )}
      </div>
    );
  }

  // Scenario 1, 3, 4: Multiple subjects - show subject selection
  if (!activeSubject) return null;

  return (
    <div className="container mx-auto space-y-12 px-4 py-8 sm:px-6 lg:px-8">
      {/* Hero Section */}
      <HeroSection heroImage={data.heroImage} hero={data.hero} isGuest={true} />

      {/* Announcements */}
      {data.announcements?.length > 0 &&
        data.announcements.map((announcement) => (
          <AnnouncementPanel
            key={announcement.id}
            announcement={announcement}
          />
        ))}

      {/* Special Offers Section */}
      {offers && offers.length > 0 && (
        <OffersCarousel
          offers={offers}
          title={plainTrans("home.specialOffers")}
          description={trans("home.offersDescription")}
        />
      )}

      {/* About Section */}
      {data.about?.content && (
        <div className="flex flex-col gap-8 rounded-xl bg-white/50 p-6 dark:bg-gray-800/50 md:flex-row md:p-8">
          <div className="flex-1">
            <h2 className="mb-3 text-3xl font-bold text-gray-800 dark:text-gray-200 md:text-4xl">
              {data.about.title}
            </h2>
            <TypewriterText
              text={data.about.content}
              speed={20}
              className="text-lg leading-relaxed text-gray-600 dark:text-gray-300"
            />
          </div>
          {data.about.image && (
            <div className="relative">
              <img
                src={data.about.image.url}
                alt="about"
                className="h-[300px] w-full rounded-lg object-cover md:h-[400px]"
              />
            </div>
          )}
        </div>
      )}

      {/* Subject Selection with Speech Bubble */}
      <div className="space-y-8">
        <SubjectSelection
          subjects={data.grades?.subjects || []}
          activeSubject={activeSubject}
          onSubjectChange={handleSubjectChange}
        />

        {/* Content Section with White Background */}
        <div className="rounded-2xl bg-white p-8 transition-colors duration-300 dark:bg-gray-800">
          <div className="space-y-12">
            {/* Grades with Lazy Loading */}
            {hasGrades &&
              data.grades?.grades?.map((grade) => (
                <InViewport offset={50} key={grade.id}>
                  <LazySessionsSection
                    key={grade.id}
                    grade={grade}
                    filter={{
                      subject: activeSubject.id,
                      grade: grade.id,
                    }}
                  />
                </InViewport>
              ))}

            {/* Categories Section (Scenario 3) */}
            {hasCategories && (
              <>
                <div className="border-t border-gray-100 pt-8 dark:border-gray-700" />
                <CategoriesSection categories={data.grades?.categories || []} />
              </>
            )}

            {/* Instructors Section (Scenario 4) */}
            {hasInstructors && (
              <>
                <div className="border-t border-gray-100 pt-8 dark:border-gray-700" />
                <InstructorsSection
                  instructors={data.grades?.instructors || []}
                />
              </>
            )}

            {/* Grades Categories Section */}
            {data.grades?.gradesCategories &&
              data.grades.gradesCategories.length > 0 && (
                <>
                  <div className="border-t border-gray-100 pt-8 dark:border-gray-700" />
                  <GradesCategoriesSection
                    gradesCategories={data.grades.gradesCategories}
                    className="space-y-12"
                    containerClassName=""
                    showBorder={false}
                  />
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
